// ============================================================================
// Block Layout Wrapper — Applies block-level layout styles
// Two-box pattern: outer Box for alignment, inner Box for visual styles.
// Returns <>{children}</> when no layout config (zero DOM overhead).
// Supports mobile overrides via flat mobile_X fields on BlockLayoutConfig.
// ============================================================================

import { useMemo, type CSSProperties } from 'react';
import { Box } from '@chakra-ui/react';
import type { BlockLayoutConfig } from '../editor/types';
import { buildResponsiveBlockLayoutStyles } from './utils/blockLayoutStyleBuilder';
import { usePreviewDevice } from './PreviewDeviceContext';

interface BlockLayoutWrapperProps {
  layout: BlockLayoutConfig | null | undefined;
  children: React.ReactNode;
}

/**
 * Build a Chakra sx object that applies mobile style overrides via @media.
 * Only includes properties that differ between desktop and mobile.
 */
function buildMobileMediaSx(
  desktopWrapper: CSSProperties,
  desktopInner: CSSProperties,
  mobileWrapper: CSSProperties,
  mobileInner: CSSProperties,
): { wrapperSx: Record<string, unknown> | null; innerSx: Record<string, unknown> | null } {
  const wrapperOverrides: Record<string, unknown> = {};
  const innerOverrides: Record<string, unknown> = {};

  // Compare wrapper styles
  if (mobileWrapper.justifyContent !== desktopWrapper.justifyContent) {
    wrapperOverrides.justifyContent = mobileWrapper.justifyContent ?? 'flex-start';
  }

  // Compare inner styles
  const innerKeys: (keyof CSSProperties)[] = ['textAlign', 'maxWidth', 'width', 'padding', 'margin'];
  for (const key of innerKeys) {
    if (mobileInner[key] !== desktopInner[key]) {
      innerOverrides[key] = mobileInner[key] ?? (key === 'width' ? undefined : key === 'maxWidth' ? 'none' : '0');
    }
  }

  return {
    wrapperSx: Object.keys(wrapperOverrides).length > 0
      ? { '@media (max-width: 768px)': wrapperOverrides }
      : null,
    innerSx: Object.keys(innerOverrides).length > 0
      ? { '@media (max-width: 768px)': innerOverrides }
      : null,
  };
}

export function BlockLayoutWrapper({ layout, children }: BlockLayoutWrapperProps) {
  // Serialize layout to detect hookstate proxy mutations that keep the same reference
  const layoutKey = layout ? JSON.stringify(layout) : '';

  const { desktop, mobile } = useMemo(
    () => buildResponsiveBlockLayoutStyles(layout),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [layoutKey],
  );
  const previewDevice = usePreviewDevice();

  // Production mobile media queries (only when not in editor preview)
  // NOTE: Must be called before the early return to respect Rules of Hooks
  const mediaSx = useMemo(() => {
    if (previewDevice || !mobile) return { wrapperSx: null, innerSx: null };
    return buildMobileMediaSx(
      desktop.wrapperStyle, desktop.innerStyle,
      mobile.wrapperStyle, mobile.innerStyle,
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [previewDevice, layoutKey]);

  // No layout config → render children directly (zero overhead)
  if (!layout) return <>{children}</>;

  // Decide which styles to use based on context
  const isEditorMobile = previewDevice === 'mobile';
  const activeStyles = (isEditorMobile && mobile) ? mobile : desktop;
  const { wrapperStyle, innerStyle, hideOn } = activeStyles;

  // Responsive visibility
  const hideMobile = hideOn.includes('mobile');
  const hideDesktop = hideOn.includes('desktop');

  let displayProp: string | Record<string, string> | undefined;

  if (previewDevice) {
    const shouldHide =
      (previewDevice === 'mobile' && hideMobile) ||
      (previewDevice === 'desktop' && hideDesktop);
    if (shouldHide) displayProp = 'none';
  } else {
    if (hideMobile && hideDesktop) {
      displayProp = 'none';
    } else if (hideMobile) {
      displayProp = { base: 'none', md: 'flex' };
    } else if (hideDesktop) {
      displayProp = { base: 'flex', md: 'none' };
    }
  }

  const hasInnerStyles = Object.keys(innerStyle).length > 0 || mediaSx.innerSx !== null;

  // When no alignment is set, let children stretch to full width
  // In editor mobile mode, check mobile alignment; in production, check desktop
  const effectiveAlignment = (isEditorMobile && mobile)
    ? (layout.mobile_alignment ?? layout.alignment)
    : layout.alignment;
  const flexChildWidth = !effectiveAlignment ? '100%' : undefined;

  return (
    <Box
      gc-testing-id="blockLayoutWrapper"
      display={displayProp ?? 'flex'}
      minW={0}
      style={wrapperStyle}
      sx={mediaSx.wrapperSx ?? undefined}
    >
      {hasInnerStyles ? (
        <Box w={flexChildWidth} style={innerStyle} sx={mediaSx.innerSx ?? undefined}>
          {children}
        </Box>
      ) : (
        flexChildWidth ? (
          <Box w={flexChildWidth}>{children}</Box>
        ) : (
          children
        )
      )}
    </Box>
  );
}
